import { createRouter, createWebHistory } from 'vue-router';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import store from '@/store'; // Adjust path to your store
import api from '@/api/axios'; // Import the globally defined Axios instance

// Auth Default Routes
const authChildRoutes = (prefix) => [
  {
    path: 'login',
    name: prefix + '.login',
    meta: { auth: false, name: 'Login', title: 'Login Page' },
    component: () => import('@/views/auth/SignIn.vue'),
  },
  {
    path: 'action',
    name: prefix + '.action',
    meta: { auth: false, name: 'Email Action Handler', title: 'Processing' },
    component: () => import('@/views/auth/ActionHandler.vue'),
  },
  {
    path: 'register',
    name: prefix + '.register',
    meta: { auth: false, name: 'Register', title: 'Register Page' },
    component: () => import('@/views/auth/SignUp.vue'),
  },
  {
    path: 'reset-password',
    name: prefix + '.reset-password',
    meta: { auth: false, name: 'Reset Password', title: 'Reset Password' },
    component: () => import('@/views/auth/ResetPassword.vue'),
  },
  {
    path: 'confirm-password-reset',
    name: prefix + '.confirm-password-reset',
    meta: { auth: false, name: 'Confirm Password Reset', title: 'Confirm Password Reset' },
    component: () => import('@/views/auth/ConfirmPasswordReset.vue'),
  },
  {
    path: 'verify-email',
    name: prefix + '.verify-email',
    meta: { auth: false, name: 'Verify Email', title: 'Verify Email' },
    component: () => import('@/views/auth/VerifyEmail.vue'),
  },
  {
    path: 'verify-succeed',
    name: prefix + '.verify-succeed',
    meta: {
      auth: false,
      name: 'Verify Email succeeded',
      title: 'Email Verification Succeeded',
    },
    component: () => import('@/views/auth/VerifySucceed.vue'),
  },
  {
    path: 'verify-failed',
    name: prefix + '.verify-failed',
    meta: {
      auth: false,
      name: 'Verify Email failed',
      title: 'Email Verification Failed',
    },
    component: () => import('@/views/auth/VerifyFailed.vue'),
  },
  {
    path: 'lock-screen',
    name: prefix + '.lock-screen',
    meta: { auth: false, name: 'Lock Screen', title: 'Lock Screen' },
    component: () => import('@/views/auth/LockScreen.vue'),
  },
  {
    path: 'training-url-error',
    name: prefix + '.training-url-error',
    meta: { auth: false, name: 'Training URL Error', title: 'Training URL Not Found' },
    component: () => import('@/views/auth/TrainingUrlError.vue'),
  },
];

const dashboardChildRoutes = (prefix) => [
  {
    path: '',
    meta: {
      auth: true,
      name: 'Dashboard',
      isBanner: true,
      title: 'Dashboard - Current Status',
      requiredRoles: ['assessor L1', 'assessor L2', 'client SME'],
    },
    component: () => import('@/views/DashboardView.vue'),
  },
];

// Default routes
const defaultChildRoutes = (prefix) => [
  {
    path: '/ElementOutro',
    name: prefix + '.elementOutro',
    meta: {
      auth: true,
      name: 'elementOutro',
      isBanner: true,
      title: 'Element Outro',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () => import('@/views/assessment/ElementOutroView.vue'),
  },
  {
    path: '',
    name: prefix + '.dashboard',
    meta: {
      auth: true,
      name: 'Dashboard',
      isBanner: true,
      title: 'Dashboard - Current Status',
      requiredRoles: ['assessor L1', 'assessor L2', 'client SME'],
    },
    component: () => import('@/views/DashboardView.vue'),
  },
  //Welcome Pages
  {
    path: '/Welcome',
    name: prefix + '.welcome',
    meta: {
      auth: true,
      name: 'Home',
      isBanner: true,
      showSubHeader: true,
      title: 'Welcome',
    },
    component: () => import('@/views/welcome/WelcomeView.vue'),
  },
  {
    path: '/FullAssessment',
    name: prefix + '.fullAssessment',
    meta: {
      auth: true,
      name: 'Home',
      isBanner: true,
      title: 'Onsite Assessment',
    },
    component: () => import('@/views/welcome/FullAssessment.vue'),
  },
  {
    path: '/SelfAssessment',
    name: prefix + '.selfAssessment',
    meta: {
      auth: true,
      name: 'Home',
      isBanner: true,
      title: 'Standard Assessment',
    },
    component: () => import('@/views/welcome/SelfAssessment.vue'),
  },
  // Assessments
  {
    path: '/CurrentAssessment',
    name: prefix + '.currentAssessment',
    meta: {
      auth: true,
      name: 'CurrentAssessment',
      isBanner: true,
      title: 'Current Assessment',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () => import('@/views/assessment/CurrentAssessment.vue'),
  },
  {
    path: '/Create-pird',
    name: prefix + '.createPird',
    meta: {
      auth: true,
      name: 'Create-pird',
      isBanner: true,
      title: 'Create Assessment PIRD',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () => import('@/views/assessment/pird/CreatePirdView.vue'),
  },
  {
    path: '/PIRDQuestionnaire',
    name: prefix + '.pirdQuestionnaire',
    meta: {
      auth: true,
      name: 'PIRDQuestionnaire',
      isBanner: true,
      title: 'PIRD Questionnaire',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () =>
      import('@/views/assessment/pird/PIRDQuestionnaireView.vue'),
  },
  {
    path: '/PIRDQuestionnaireViewer',
    name: prefix + '.pirdQuestionnaireViewer',
    meta: {
      auth: true,
      name: 'PIRDQuestionnaireViewer',
      isBanner: true,
      title: 'PIRD Questionnaire Viewer',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () =>
      import('@/views/assessment/pird/PIRDQuestionnaireViewerView.vue'),
  },
  {
    path: '/CalibrationReview',
    name: prefix + '.calibrationReview',
    meta: {
      auth: true,
      name: 'CalibrationReview',
      isBanner: true,
      title: 'Calibration and Review Pillar',
      requiredRoles: ['assessor L1', 'assessor L2'],
    },
    component: () =>
      import('@/views/assessment/CalibrationReviewPillarView.vue'),
  },
  {
    path: '/CalibrationReview/Questions',
    name: prefix + '.calibrationReviewQuestions',
    meta: {
      auth: true,
      name: 'calibrationReviewQuestions',
      isBanner: true,
      title: 'Calibration and Review Questions',
      requiredRoles: ['assessor L1', 'assessor L2'],
    },
    component: () =>
      import('@/views/assessment/CalibrationReviewQuestionsView.vue'),
  },
  {
    path: '/EditAssessmentScore',
    name: prefix + '.editAssessmentScore',
    meta: {
      auth: true,
      name: 'EditAssessmentScore',
      isBanner: true,
      title: 'Edit Assessment Assessor Score',
      requiredRoles: ['assessor L1', 'assessor L2'],
    },
    component: () => import('@/views/assessment/EditAssessmentScoreView.vue'),
  },
  {
    path: '/PillarAssessment',
    name: prefix + '.pillarAssessment',
    meta: {
      auth: true,
      name: 'pillarAssessment',
      isBanner: true,
      title: 'Pillar Assessment',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () => import('@/views/assessment/PillarAssessment.vue'),
  },
  {
    path: '/TrainingPillarAssessment',
    name: prefix + '.trainingPillarAssessment',
    meta: {
      auth: true,
      name: 'trainingPillarAssessment',
      isBanner: true,
      title: 'Training Pillar Assessment',
    },
    component: () => import('@/views/assessment/TrainingPillarAssessment.vue'),
  },
  {
    path: '/Questions',
    name: prefix + '.questions',
    meta: {
      auth: true,
      name: 'Questions',
      isBanner: true,
      title: 'Questions',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () => import('@/views/assessment/QuestionsView.vue'),
  },
  {
    path: '/Questionnaire',
    name: prefix + '.questionnaire',
    meta: {
      auth: true,
      name: 'Questionnaire',
      isBanner: true,
      title: 'Questionnaire',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () => import('@/views/assessment/QuestionnaireView.vue'),
  },
  {
    path: '/EditReport',
    name: prefix + '.editReport',
    meta: {
      auth: true,
      name: 'EditReport',
      isBanner: true,
      title: 'Edit Report',
    },
    component: () => import('@/views/assessment/EditReport.vue'),
  },
  {
    path: '/CompletedAssessment',
    name: prefix + '.CompletedAssessment',
    meta: {
      auth: true,
      name: 'CompletedAssessment',
      isBanner: true,
      title: 'Completed Assessment',
      requiredRoles: ['assessor L1', 'assessor L2', 'client SME'],
    },
    component: () => import('@/views/assessment/CompletedAssessment.vue'),
  },
  // Reports
  {
    path: '/Reports',
    name: prefix + '.reports',
    meta: { auth: true, name: 'reports', isBanner: true, title: 'Reports' },
    component: () => import('@/views/ReportsView.vue'),
  },
  // Actions
  {
    path: '/Actions',
    name: prefix + '.actions',
    meta: {
      auth: true,
      name: 'actions',
      isBanner: true,
      title: 'Actions',
      requiredRoles: ['assessor L1', 'assessor L2', 'client SME'],
    },
    component: () => import('@/views/assessment/ActionsPage.vue'),
  },
  // Settings
  {
    path: '/MyAccount',
    name: prefix + '.myAccount',
    meta: {
      auth: true,
      name: 'MyAccount',
      isBanner: true,
      title: 'My Account',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
        'administrator',
      ],
    },
    component: () => import('@/views/settings/MyAccount.vue'),
  },
  {
    path: '/Notifications',
    name: prefix + '.notifications',
    meta: {
      auth: true,
      name: 'Notifications',
      isBanner: true,
      title: 'Notifications',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
        'administrator',
      ],
    },
    component: () => import('@/views/settings/Notifications.vue'),
  },
  {
    path: '/PaymentAndSubscription',
    name: prefix + '.paymentSubscription',
    meta: {
      auth: true,
      name: 'paymentSubscription',
      isBanner: true,
      title: 'Payment and Subscription',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
        'administrator',
      ],
    },
    component: () => import('@/views/settings/PaymentAndSubscription.vue'),
  },
  // Support
  {
    path: '/PremaStandards',
    name: prefix + '.premaStandards',
    meta: {
      auth: true,
      name: 'PremaStandards',
      isBanner: true,
      title: 'PREMA Standards',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
        'administrator',
      ],
    },
    component: () => import('@/views/support/PremaStandards.vue'),
  },
  {
    path: '/GettingStarted',
    name: prefix + '.gettingStarted',
    meta: {
      auth: true,
      name: 'GettingStarted',
      isBanner: true,
      title: 'Getting Started',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () => import('@/views/support/GettingStarted.vue'),
  },
  {
    path: '/supportFaq',
    name: prefix + '.supportFaq',
    meta: {
      auth: true,
      name: 'FAQ',
      isBanner: true,
      title: 'FAQ and Guides',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () => import('@/views/support/Faq.vue'),
  },
  {
    path: '/ServiceRequest',
    name: prefix + '.serviceRequest',
    meta: {
      auth: true,
      name: 'ServiceRequest',
      isBanner: true,
      title: 'Service Request',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () => import('@/views/support/MyServiceRequest.vue'),
  },
  {
    path: '/ContactUs',
    name: prefix + '.contactUs',
    meta: {
      auth: true,
      name: 'GetInTouch',
      isBanner: true,
      title: 'Contact Us',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () => import('@/views/support/MyServiceRequest.vue'),
  },
  {
    path: '/WhatsNew',
    name: prefix + '.whatsNew',
    meta: {
      auth: true,
      name: 'whatsNew',
      isBanner: true,
      title: "What's New",
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () => import('@/views/support/WhatsNew.vue'),
  },
  {
    path: '/PremaTraining',
    name: prefix + '.premaTraining',
    meta: {
      auth: true,
      name: 'PremaTraining',
      isBanner: true,
      title: 'PREMA Training',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
        'administrator',
      ],
    },
    component: () => import('@/views/PremaTraining.vue'),
  },
  {
    path: '/AssessmentsAdmin',
    name: prefix + '.assessmentsAdmin',
    meta: {
      auth: true,
      name: 'assessmentsAdmin',
      isBanner: true,
      title: 'Assessments - Admin Panel',
      requiredRoles: ['administrator'],
    },
    component: () => import('@/views/admin-panel/AssessmentsAdminView.vue'),
  },
  {
    path: '/Organisations',
    name: prefix + '.organisations',
    meta: {
      auth: true,
      name: 'organisations',
      isBanner: true,
      title: 'Organisations - Admin Panel',
      requiredRoles: ['administrator'],
    },
    component: () => import('@/views/admin-panel/OrganisationsView.vue'),
  },
  {
    path: '/PaymentsSubAdmin',
    name: prefix + '.paymentsSubAdmin',
    meta: {
      auth: true,
      name: 'paymentsSubAdmin',
      isBanner: true,
      title: 'Payments and Subscriptions - Admin Panel',
      requiredRoles: ['administrator'],
    },
    component: () => import('@/views/admin-panel/PaymentsSubAdminView.vue'),
  },
  {
    path: '/Users',
    name: prefix + '.users',
    meta: {
      auth: true,
      name: 'users',
      isBanner: true,
      title: 'Users - Admin Panel',
      requiredRoles: ['administrator'],
    },
    component: () => import('@/views/admin-panel/UsersView.vue'),
  },
  {
    path: '/faq',
    name: prefix + '.faq',
    meta: {
      auth: true,
      name: 'faq',
      isBanner: true,
      title: 'FAQ - Admin Panel',
      requiredRoles: ['administrator'],
    },
    component: () => import('@/views/admin-panel/Faq.vue'),
  },
  {
    path: '/documentLibrary',
    name: prefix + '.documentLibrary',
    meta: {
      auth: true,
      name: 'faq',
      isBanner: true,
      title: 'Document Library - Admin Panel',
      requiredRoles: ['administrator'],
    },
    component: () => import('@/views/admin-panel/DocumentLibrary.vue'),
  },
  {
    path: '/trainingSessions',
    name: prefix + '.trainingSessions',
    meta: {
      auth: true,
      name: 'trainingSessions',
      isBanner: true,
      title: 'Training Sessions - Admin Panel',
      requiredRoles: ['administrator'],
    },
    component: () => import('@/views/admin-panel/TrainingSessions.vue'),
  },

  /************************************/

  {
    path: '/user-list',
    name: prefix + '.user-list',
    meta: {
      auth: true,
      name: 'User List',
      isBanner: true,
    },
    component: () => import('@/views/trash/ListPage.vue'),
  },
  {
    path: '/user-add',
    name: prefix + '.user-add',
    meta: {
      auth: true,
      name: 'User Add',
      isBanner: true,
    },
    component: () => import('@/views/trash/AddPage.vue'),
  },
  {
    path: '/user-profile',
    name: prefix + '.user-profile',
    meta: { auth: true, name: 'User Add', isBanner: true },
    component: () => import('@/views/trash/ProfilePage.vue'),
  },
  {
    path: '/privacy-setting',
    name: prefix + '.user-privacy-setting',
    meta: {
      auth: true,
      name: 'Privacy Setting',
      isBanner: true,
    },
    component: () => import('@/views/trash/PrivacySetting.vue'),
  },
  // Table Pages
  {
    path: '/datatable',
    name: prefix + '.data-table',
    meta: { auth: true, name: 'Data Table', isBanner: true },
    component: () => import('@/views/trash/DataTable.vue'),
  },
  // Icons Pages
  {
    path: '/icons/dual-tone',
    name: prefix + '.icons.dual-tone',
    meta: { auth: true, name: 'Dual Tone Icon', isBanner: true },
    component: () => import('@/views/trash/DualToneIcon.vue'),
  },
  // Extra Pages
  {
    path: '/privacy-policy',
    name: prefix + '.privacy-policy',
    meta: { auth: true, name: 'Privacy Policy', isBanner: true },
    component: () => import('@/views/trash/PrivacyPolicy.vue'),
  },
  {
    path: '/terms-and-conditions',
    name: prefix + '.terms-and-conditions',
    meta: { auth: true, name: 'Terms and Conditions', isBanner: true },
    component: () => import('@/views/trash/TermsAndConditions.vue'),
  },
  {
    path: '/admin-permissions',
    name: prefix + '.admin-permissions',
    meta: { auth: true, name: 'Admin Permissions', isBanner: true },
    component: () => import('@/views/trash/AdminPage.vue'),
  },
  {
    path: '/ElementInfo',
    name: prefix + '.elementInfo',
    meta: {
      auth: true,
      name: 'elementInfo',
      isBanner: true,
      title: 'Element Info',
      requiredRoles: [
        'assessor L1',
        'assessor L2',
        'client SME',
        'client End User',
      ],
    },
    component: () => import('@/views/assessment/ElementInfoView.vue'),
  },
];

const errorRoutes = (prefix) => [
  // Error Pages
  {
    path: '404',
    name: prefix + '.404',
    meta: { auth: true, name: 'Error 404', isBanner: true },
    component: () => import('@/views/trash/Error404Page.vue'),
  },
  {
    path: '500',
    name: prefix + '.500',
    meta: { auth: true, name: 'Error 500', isBanner: true },
    component: () => import('@/views/trash/Error500Page.vue'),
  },
  {
    path: 'maintenance',
    name: prefix + '.maintenance',
    meta: { auth: true, name: 'Maintenance', isBanner: true },
    component: () => import('@/views/trash/MaintenancePage.vue'),
  },
];

const routes = [
  {
    path: '/',
    redirect: '/auth/login',
  },
  // Training Login Route
  {
    path: '/training',
    name: 'trainingLogin',
    meta: { auth: false, name: 'Training Login', title: 'Training Login Page' },
    component: () => import('@/views/auth/TrainingLogin.vue'),
  },
  // Default Pages
  {
    path: '/Dashboard',
    name: 'dashboard',
    component: () => import('../components/layouts/DefaultLayout.vue'),
    children: defaultChildRoutes('default'),
  },
  // Auth Skins
  {
    path: '/auth',
    name: 'auth',
    component: () => import('../components/layouts/BlankLayout.vue'),
    children: authChildRoutes('auth'),
  },
  // Errors Pages
  {
    path: '/errors',
    name: 'errors',
    component: () => import('../components/layouts/BlankLayout.vue'),
    children: errorRoutes('errors'),
  },
  // Catch-all route for not found pages
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    meta: { auth: true },
    component: () => import('@/views/trash/Error404Page.vue'),
  },
];

const router = createRouter({
  linkActiveClass: 'active',
  linkExactActiveClass: 'exact-active',
  history: createWebHistory(process.env.BASE_URL),
  base: process.env.BASE_URL,
  routes,
});

// Helper function to check if user is trainee
const isTrainee = () => {
  return store.state.isTrainingUser || !!localStorage.getItem('trainingUsername');
};

// Handle training user authentication and route access
const handleTrainingUserAuth = async (to, from, next, localAccessToken, localRefreshToken, localUsername, localPassword) => {
  // Check if user is trying to access URLs outside of training paths (e.g., /tr paths)
  // But skip if we're already on the training login route to avoid infinite redirects
  if (to.path.startsWith('/tr') && to.path !== '/training') {
    // Redirect to the last valid path or fallback to training page
    const lastValidPath = sessionStorage.getItem('lastValidPath');
    const redirectPath = lastValidPath || '/training';
    return next(redirectPath);
  }

  // If user data is missing from store, restore it from localStorage credentials
  if (!store.state.user) {
    try {
      // Make API call with saved credentials to restore user and assessment data
      const authResponse = await api.post('/auth', {
        username: localUsername,
        password: localPassword,
      });

      const { user, access_token, refresh_token } = authResponse.data;
      const userId = user.id;

      // Update tokens in Vuex and localStorage (in case they were refreshed)
      store.commit('auth/setAccessToken', access_token);
      store.commit('auth/setRefreshToken', refresh_token);
      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);

      // Set user in store
      const userData = {
        user: {
          ...user,
        },
      };
      store.commit('setUser', userData);

      // Fetch user assessments
      const assessmentsResponse = await api.get(
        `/user_assessments?userId=${userId}`
      );

      const userAssessments =
        assessmentsResponse.data.user_assessments || [];

      if (userAssessments.length > 0) {
        // Find the most recent assessment
        const mostRecentAssessment = userAssessments.reduce(
          (latest, current) => {
            const latestDate = new Date(latest.inserted_at).getTime();
            const currentDate = new Date(current.inserted_at).getTime();
            return currentDate > latestDate ? current : latest;
          }
        );

        const assessmentId = mostRecentAssessment.assessment.id;
        const userAssessmentId = mostRecentAssessment.id;

        // Store assessment info in sessionStorage for later use
        sessionStorage.setItem('assessmentId', assessmentId);
        sessionStorage.setItem('userAssessmentId', userAssessmentId);
        sessionStorage.setItem('trainingUrl', `/TrainingPillarAssessment?assessmentId=${assessmentId}&userAssessmentId=${userAssessmentId}`);
      }
    } catch (error) {
      console.error(
        'Failed to restore training user session from localStorage:',
        error
      );
      // Clear training user session if credentials fail
      store.commit('setIsTrainingUser', false);
      store.commit('auth/setAccessToken', null);
      store.commit('auth/setRefreshToken', null);
      store.commit('setUser', null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('trainingUsername');
      localStorage.removeItem('trainingPassword');
    }
  }

  // Check route access for training users
  const accessToken = store.state.auth?.accessToken || localAccessToken;

  // List of allowed routes for training users
  const allowedTrainingRoutes = [
    'default.trainingPillarAssessment',
    'default.elementInfo',
    'default.elementOutro',
    'default.questions',
    'default.questionnaire',
    'errors.404', // Allow error pages for training users
  ];

  if (to.meta.auth) {
    // Check if route is not found for trainees
    if (to.name === 'not-found') {
      // Redirect to the last valid path or fallback to training page
      const lastValidPath = sessionStorage.getItem('lastValidPath');
      const redirectPath = lastValidPath || '/training';
      return next(redirectPath);
    }
    if (accessToken) {
      // Check if the route is in the allowed list
      if (allowedTrainingRoutes.includes(to.name)) {
        // Save the training URL
        sessionStorage.setItem('trainingUrl', to.fullPath);
        // Save last valid visited path for training users (including query parameters)
        if (to.path !== '/') {
          sessionStorage.setItem('lastValidPath', to.fullPath);
        }
        return next();
      } else {
        // Route not allowed for training users
        // Redirect to the last valid path or fallback to training page
        const lastValidPath = sessionStorage.getItem('lastValidPath');
        const redirectPath = lastValidPath || '/training';
        return next(redirectPath);
      }
    } else {
      // Log out the training user if no token is found
      store.commit('setIsTrainingUser', false);
      store.commit('auth/setAccessToken', null);
      store.commit('auth/setRefreshToken', null);
      store.commit('setUser', null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('trainingUsername');
      localStorage.removeItem('trainingPassword');
      sessionStorage.removeItem('assessmentId');
      sessionStorage.removeItem('userAssessmentId');
      return next('/training');
    }
  } else {
    // Non-auth routes are accessible to training users
    return next();
  }
};

const authGuard = async (to, from, next) => {
  const defaultTitle = 'prema | your partner for excellence.';
  document.title = to.meta.title
    ? `${to.meta.title} - ${defaultTitle}`
    : defaultTitle;

  // Check localStorage for training user tokens and credentials on app load
  const localAccessToken = localStorage.getItem('accessToken');
  const localRefreshToken = localStorage.getItem('refreshToken');
  const localUsername = localStorage.getItem('trainingUsername');
  const localPassword = localStorage.getItem('trainingPassword');

  if (
    localAccessToken &&
    localRefreshToken &&
    localUsername &&
    localPassword &&
    !store.state.isTrainingUser
  ) {
    // Restore training user session from localStorage
    store.commit('setIsTrainingUser', true);
    store.commit('auth/setAccessToken', localAccessToken);
    store.commit('auth/setRefreshToken', localRefreshToken);
  }

  // Check if user is trying to access /tr paths (training login or related)
  // This should happen before checking authentication state
  // But skip this check if we're already on the training login route to avoid infinite redirects
  if (to.path.startsWith('/tr') && to.path !== '/training') {
    // Check if user is authenticated
    const currentUser = await new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
        unsubscribe();
        resolve(user);
      });
    });
    
    // If not authenticated, redirect to training error page
    if (!currentUser && !isTrainee()) {
      return next('/auth/training-url-error');
    }
  }

  // Handle training users separately
  if (isTrainee()) {
    // Check if user is trying to access URLs outside of the training paths
    // The check for /tr paths should happen after we check if they're trying to access non-training routes
    return handleTrainingUserAuth(to, from, next, localAccessToken, localRefreshToken, localUsername, localPassword);
  }

  // Handle non-authenticated users
  if (to.meta.auth) {
    try {
      const auth = getAuth();
      // Wrap `onAuthStateChanged` in a Promise so we can use async/await cleanly
      const currentUser = await new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(
          auth,
          (user) => {
            unsubscribe(); // Stop listening after we get the auth state
            resolve(user);
          },
          reject
        );
      });

      // Check if user is authenticated
      if (!currentUser) {
        // User is not authenticated
        // Check if they're trying to access a /tr path (but not the training login page itself)
        if (to.path.startsWith('/tr') && to.path !== '/training') {
          return next('/auth/training-url-error');
        }
        // Redirect to login for any other protected route
        return next('/auth/login');
      }

      // Ensure tokens are available in the Vuex store
      let accessToken = store.state.auth.accessToken;
      let refreshToken = store.state.auth.refreshToken;
      if (!accessToken || !refreshToken) {
        const idToken = await currentUser.getIdToken();
        // Request new tokens using Firebase ID token
        const tokenResponse = await api.post('/auth', { token: idToken });
        accessToken = tokenResponse.data.access_token;
        refreshToken = tokenResponse.data.refresh_token;
        // Save tokens in the Vuex store
        store.commit('auth/setAccessToken', accessToken);
        store.commit('auth/setRefreshToken', refreshToken);
      }

      // Ensure user data is available in Vuex store
      if (!store.state.user) {
        const userInfo = await store.dispatch('fetchUser', {
          api,
          email: currentUser.email,
        });
        store.commit('setUser', userInfo);
      }

      // Check role-based access
      const userRoles = store.state.user.user.roles.map((role) => role.name); // Map roles to get an array of role names
      const requiredRoles = to.meta.requiredRoles || []; // Define required roles in route meta

      // Check if any of the required roles are included in the user's roles
      const hasRequiredRole = requiredRoles.some((role) =>
        userRoles.includes(role)
      );

      if (requiredRoles.length > 0 && !hasRequiredRole) {
        if (store.state.user.user.is_admin) {
          return next('/Users');
        } else {
          // No permissions: redirect to last valid path
          const lastValidPath = sessionStorage.getItem('lastValidPath');
          const fallbackPath = from.path && from.path !== '/' ? from.path : '/Dashboard';
          const redirectPath = lastValidPath || fallbackPath;
          return next(redirectPath);
        }
      }

      // Check subscription requirement for PREMA Standards route
      if (to.name === 'default.premaStandards') {
        const userOrganisations = store.state.user?.user?.organisations || [];
        const hasActiveSubscription = userOrganisations.some((org) => {
          return org?.organisation?.subscription?.status === 'active';
        });

        if (!hasActiveSubscription) {
          // No active subscription: redirect to subscription page
          return next({ name: 'default.paymentSubscription' });
        }
      }

      // Check if route is not found
      if (to.name === 'not-found') {
        // Wrong URL: redirect to last valid path or previous or default
        const lastValidPath = sessionStorage.getItem('lastValidPath');
        const fallbackPath = from.path && from.path !== '/' ? from.path : '/Dashboard';
        const redirectPath = lastValidPath || fallbackPath;
        return next(redirectPath);
      }

      // Save last valid visited path (only for valid routes)
      if (to.name !== 'not-found' && to.path !== '/') {
        sessionStorage.setItem('lastValidPath', to.fullPath);
      }

      // If everything passes, proceed to the route
      next();
    } catch (error) {
      console.error('Router guard error:', error);
      // On error, check if trying to access /tr path (but not the training login page itself)
      if (to.path.startsWith('/tr') && to.path !== '/training') {
        return next('/auth/training-url-error');
      }
      next('/auth/login'); // Redirect on unexpected errors
    }
  } else {
    // For routes that don't require auth, allow access
    next();
  }
};

// Apply the guard to all routes
router.beforeEach(authGuard);

export default router;